import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { AccessTokenService, SpendkeyStaffLevel } from './access-token.service';
import { AlertService } from './alert.service';

/**
 * Route guard for every Spendkey-staff-only surface, in two layers:
 *   1. A Spendkey-staff email (`@spendkey.io` / `@spendkey.co.uk`), AND
 *   2. The role the surface needs — `'any'` admits every role including
 *      Member, `'admin'` needs the JWT `role` claim Admin or SuperAdmin.
 *
 * Both layers are `AccessTokenService.isSpendkeyStaff(level)`, the same check
 * menus use to hide a link, so a link is never offered to someone the route
 * would then refuse. RBAC is not consulted — staff access is the email-domain
 * and role axis on every tenant. The API enforces the same two layers with
 * `[RequireSpendkeyStaff]`.
 *
 * Synchronous throughout — no RBAC slice to hydrate, so no misroute window.
 * Unauthenticated / expired tokens short-circuit to `/login` (session expiry,
 * not "access denied"). Everyone else who fails is sent to `denyRedirect` with
 * the standard access-denied toast.
 *
 * @param level `'any'` (default) or `'admin'`.
 * @param denyRedirect Where to send a denied caller. Never point this at a
 *   Spendkey-only route.
 * @returns A `CanActivateFn`.
 */
export function spendkeyStaffGuard(
  level: SpendkeyStaffLevel = 'any',
  denyRedirect = 'dashboard',
): CanActivateFn {
  return () => {
    const accessTokenService = inject(AccessTokenService);
    const router = inject(Router);
    const alertService = inject(AlertService);

    if (!accessTokenService.isTokenAvailable() || accessTokenService.isTokenExpired()) {
      router.navigateByUrl('login');
      return false;
    }

    if (accessTokenService.isSpendkeyStaff(level)) {
      return true;
    }

    router.navigateByUrl(denyRedirect);
    alertService.openAlert('Access denied', 'error');
    return false;
  };
}
